/**
 * Centralized sound effects configuration for the Home Run game
 * This ensures consistent audio management across all game components
 *
 * Recommended format: MP3 at 64-96kbps for optimal balance of quality and file size
 * Location: public/sounds/ (organized by category)
 */

export interface SoundEffect {
  id: string;
  path: string;
  volume?: number; // 0.0 to 1.0, defaults to 1.0
  category: 'game' | 'player' | 'items' | 'ui';
  description: string;
}

/**
 * Game Flow Sound Effects
 * Sounds related to game state transitions and timing
 */
export const GAME_SOUNDS: Record<string, SoundEffect> = {
  // Countdown & Start
  COUNTDOWN_TICK: {
    id: 'countdown_tick',
    path: '/sounds/game/countdown_tick.mp3',
    volume: 0.7,
    category: 'game',
    description: '3-2-1 countdown tick sound',
  },
  GAME_START: {
    id: 'game_start',
    path: '/sounds/game/game_start_ok.mp3',
    volume: 0.8,
    category: 'game',
    description: 'Game/round start fanfare',
  },

  // Question Phase
  QUESTION_APPEAR: {
    id: 'question_appear',
    path: '/sounds/game/question_appear.mp3',
    volume: 0.6,
    category: 'game',
    description: 'Question appears on screen',
  },

  // Timer Alerts
  TIMER_WARNING: {
    id: 'timer_warning',
    path: '/sounds/game/timer_warning.mp3',
    volume: 0.8,
    category: 'game',
    description: 'Timer warning at 3 seconds remaining',
  },
  TIME_UP: {
    id: 'time_up',
    path: '/sounds/game/time_up.mp3',
    volume: 0.7,
    category: 'game',
    description: 'Time expired sound',
  },

  // Round Completion
  ROUND_END: {
    id: 'round_end',
    path: '/sounds/game/round_end.mp3',
    volume: 0.6,
    category: 'game',
    description: 'Round completion',
  },

  // Game Completion
  GAME_COMPLETE: {
    id: 'game_complete',
    path: '/sounds/game/game_complete.mp3',
    volume: 0.8,
    category: 'game',
    description: 'All questions finished',
  },
  VICTORY_FANFARE: {
    id: 'victory_fanfare',
    path: '/sounds/game/victory_fanfare.mp3',
    volume: 0.9,
    category: 'game',
    description: 'Winner announcement',
  },
} as const;

/**
 * Player Action Sound Effects
 * Sounds triggered by player movements and interactions
 */
export const PLAYER_SOUNDS: Record<string, SoundEffect> = {
  // Movement
  JUMP: {
    id: 'jump',
    path: '/sounds/player/jump.mp3',
    volume: 0.5,
    category: 'player',
    description: 'Character jump/hop sound',
  },
  FOOTSTEP: {
    id: 'footstep',
    path: '/sounds/player/footstep.mp3',
    volume: 0.3,
    category: 'player',
    description: 'Light footstep for running',
  },

  // Answer Selection
  ANSWER_SELECT: {
    id: 'answer_select',
    path: '/sounds/player/answer_select_sima_sima.mp3',
    volume: 0.4,
    category: 'player',
    description: 'Player reaches answer zone',
  },

  // Results
  CORRECT_ANSWER: {
    id: 'correct_answer',
    path: '/sounds/player/correct_answer_OOOHOOOO.mp3',
    volume: 0.8,
    category: 'player',
    description: 'Correct answer celebration',
  },
  WRONG_ANSWER: {
    id: 'wrong_answer',
    path: '/sounds/player/wrong_answer_omomom.mp3',
    volume: 0.1,
    category: 'player',
    description: 'Incorrect answer feedback',
  },

  // Achievements
  FIRST_PLACE: {
    id: 'first_place',
    path: '/sounds/player/first_place.mp3',
    volume: 0.9,
    category: 'player',
    description: 'First to answer correctly',
  },
  SPEED_BONUS: {
    id: 'speed_bonus',
    path: '/sounds/player/speed_bonus.mp3',
    volume: 0.7,
    category: 'player',
    description: 'Speed bonus awarded',
  },
} as const;

/**
 * Bonus Item Sound Effects
 * Sounds for power-ups and special items
 */
export const ITEM_SOUNDS: Record<string, SoundEffect> = {
  // Net Item
  NET_THROW: {
    id: 'net_throw',
    path: '/sounds/items/net_throw_PUSH.mp3',
    volume: 0.7,
    category: 'items',
    description: 'Net being thrown',
  },
  NET_HIT: {
    id: 'net_hit',
    path: '/sounds/items/net_hit_masamana_happy.mp3',
    volume: 0.8,
    category: 'items',
    description: 'Net captures player',
  },
  NET_MISS: {
    id: 'net_miss',
    path: '/sounds/items/net_miss_fart.mp3',
    volume: 0.5,
    category: 'items',
    description: 'Net misses target',
  },

  // Turbo Boost
  TURBO_ACTIVATE: {
    id: 'turbo_activate',
    path: '/sounds/items/turbo_activate_pushiii.mp3',
    volume: 0.8,
    category: 'items',
    description: 'Turbo boost activated',
  },
  TURBO_RUNNING: {
    id: 'turbo_running',
    path: '/sounds/items/turbo_running.mp3',
    volume: 0.6,
    category: 'items',
    description: 'Turbo boost active (loopable)',
  },

  // Speed Shoes
  SPEED_SHOES_EQUIP: {
    id: 'speed_shoes_equip',
    path: '/sounds/items/speed_shoes_equip_MASMANAMA.mp3',
    volume: 0.7,
    category: 'items',
    description: 'Speed shoes equipped',
  },

  // Grenade
  GRENADE_THROW: {
    id: 'grenade_throw',
    path: '/sounds/items/grenade_throw.mp3',
    volume: 0.7,
    category: 'items',
    description: 'Grenade being thrown',
  },
  GRENADE_EXPLODE: {
    id: 'grenade_explode',
    path: '/sounds/items/grenade_explode.mp3',
    volume: 0.9,
    category: 'items',
    description: 'Grenade explosion',
  },

  // Shop
  ITEM_PURCHASE: {
    id: 'item_purchase',
    path: '/sounds/items/item_purchase.mp3',
    volume: 0.7,
    category: 'items',
    description: 'Item purchased from shop',
  },
  INSUFFICIENT_COINS: {
    id: 'insufficient_coins',
    path: '/sounds/items/insufficient_coins.mp3',
    volume: 0.6,
    category: 'items',
    description: 'Not enough coins',
  },
} as const;

/**
 * UI Interaction Sound Effects
 * Sounds for buttons, navigation, and interface feedback
 */
export const UI_SOUNDS: Record<string, SoundEffect> = {
  // Buttons
  BUTTON_CLICK: {
    id: 'button_click',
    path: '/sounds/ui/button_click.mp3',
    volume: 0.5,
    category: 'ui',
    description: 'Standard button click',
  },
  BUTTON_HOVER: {
    id: 'button_hover',
    path: '/sounds/ui/button_hover.mp3',
    volume: 0.3,
    category: 'ui',
    description: 'Button hover feedback',
  },

  // Game Lobby
  PLAYER_JOIN: {
    id: 'player_join',
    path: '/sounds/ui/player_join.mp3',
    volume: 0.6,
    category: 'ui',
    description: 'New player joins lobby',
  },
  PLAYER_READY: {
    id: 'player_ready',
    path: '/sounds/ui/player_ready.mp3',
    volume: 0.5,
    category: 'ui',
    description: 'Player marks ready',
  },

  // Notifications
  SUCCESS: {
    id: 'success',
    path: '/sounds/ui/success.mp3',
    volume: 0.6,
    category: 'ui',
    description: 'Success notification',
  },
  ERROR: {
    id: 'error',
    path: '/sounds/ui/error.mp3',
    volume: 0.6,
    category: 'ui',
    description: 'Error notification',
  },

  // Special
  ACHIEVEMENT_UNLOCK: {
    id: 'achievement_unlock',
    path: '/sounds/ui/achievement_unlock.mp3',
    volume: 0.8,
    category: 'ui',
    description: 'Achievement/milestone unlocked',
  },

  // Character Selection Sounds
  CHARACTER_SELECT_0: {
    id: 'character_select_0',
    path: '/sounds/ui/character_select_0.mp3',
    volume: 0.6,
    category: 'ui',
    description: 'Coral Buddy selection (bright, bubbly)',
  },
  CHARACTER_SELECT_1: {
    id: 'character_select_1',
    path: '/sounds/ui/character_select_1.mp3',
    volume: 0.6,
    category: 'ui',
    description: 'Ocean Friend selection (watery, flowing)',
  },
  CHARACTER_SELECT_2: {
    id: 'character_select_2',
    path: '/sounds/ui/character_select_2.mp3',
    volume: 0.6,
    category: 'ui',
    description: 'Purple Pal selection (mysterious, whimsical)',
  },
  CHARACTER_SELECT_3: {
    id: 'character_select_3',
    path: '/sounds/ui/character_select_3.mp3',
    volume: 0.6,
    category: 'ui',
    description: 'Forest Mate selection (natural, earthy)',
  },
  CHARACTER_SELECT_4: {
    id: 'character_select_4',
    path: '/sounds/ui/character_select_4.mp3',
    volume: 0.6,
    category: 'ui',
    description: 'Midnight Companion selection (deep, subtle)',
  },
  CHARACTER_SELECT_5: {
    id: 'character_select_5',
    path: '/sounds/ui/character_select_5.mp3',
    volume: 0.6,
    category: 'ui',
    description: 'Sunny Sidekick selection (cheerful, energetic)',
  },
} as const;

/**
 * All sound effects combined for easy iteration
 */
export const ALL_SOUNDS: SoundEffect[] = [
  ...Object.values(GAME_SOUNDS),
  ...Object.values(PLAYER_SOUNDS),
  ...Object.values(ITEM_SOUNDS),
  ...Object.values(UI_SOUNDS),
];

/**
 * Get sound effect by ID across all categories
 */
export function getSoundById(id: string): SoundEffect | undefined {
  return ALL_SOUNDS.find(sound => sound.id === id);
}

/**
 * Get all sounds in a specific category
 */
export function getSoundsByCategory(category: SoundEffect['category']): SoundEffect[] {
  return ALL_SOUNDS.filter(sound => sound.category === category);
}

/**
 * Volume presets for different gameplay contexts
 */
export const VOLUME_PRESETS = {
  muted: 0,
  quiet: 0.3,
  normal: 0.7,
  loud: 1.0,
} as const;

/**
 * Default global volume settings
 */
export const DEFAULT_VOLUME_SETTINGS = {
  master: VOLUME_PRESETS.normal,
  game: VOLUME_PRESETS.normal,
  player: VOLUME_PRESETS.normal,
  items: VOLUME_PRESETS.normal,
  ui: VOLUME_PRESETS.normal,
} as const;

/**
 * Get character-specific selection sound by character ID (0-5)
 */
export function getCharacterSound(characterId: number): SoundEffect | undefined {
  const soundKey = `CHARACTER_SELECT_${characterId}` as keyof typeof UI_SOUNDS;
  return UI_SOUNDS[soundKey];
}
